<template>
  <div ref="pageRef" class="messages-page">
    <UserAppChrome back-mode="history" :show-dock="false" />

    <main ref="conversationContainer" class="messages-shell" aria-label="Conversazione con l'assistenza tecnica">
      <p v-if="ticketStatus === 'Completato'" class="completed-text">
        Conversazione completata. Invia un nuovo messaggio per riaprirla.
      </p>

      <ul class="ticket-container">
        <li
          v-for="(item, index) in mergedConversations"
          :key="item.messageKey || item.id || index"
          :ref="el => setLastMessageEl(el, index, mergedConversations.length)"
          class="chat-message"
          :class="`chat-message--${messageKind(item)}`"
        >
          <div class="chat-message__author">
            <span class="material-icons" aria-hidden="true">{{ messageIcon(item) }}</span>
            <strong>{{ messageAuthor(item) }}</strong>
          </div>

          <div v-if="item.content" class="chat-message__content">
            <span v-html="sanitizeMessageContent(item.content)"></span>
          </div>

          <a
            v-if="item.fileUrl && item.visibilityProfile"
            :href="item.fileUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="attached-file"
          >
            <span class="material-icons" aria-hidden="true">description</span>
            <span>
              <small>File allegato</small>
              <strong>{{ item.fileName }}</strong>
            </span>
            <span class="material-icons" aria-hidden="true">north_east</span>
          </a>

          <time :datetime="messageDateTime(item.timestamp)">{{ formatMessageDate(item.timestamp) }}</time>
          <div v-if="item.deliveryState" class="chat-message__delivery" :data-state="item.deliveryState" role="status">
            <span>{{ item.deliveryLabel }}</span>
            <button v-if="item.canRetry" type="button" :disabled="isSending || isUploading" @click="retryMessage(item.id)">Riprova</button>
          </div>
        </li>
      </ul>

      <p v-if="uploadError" class="upload-error" role="alert">{{ uploadError }}</p>
      <p v-if="connectionError" class="upload-error" role="alert">{{ connectionError }}</p>
    </main>

    <div ref="composerRef" class="chat-composer">
      <p v-if="deliveryStatus || isUploading" class="chat-composer__status" role="status" aria-live="polite" aria-atomic="true">
        {{ isUploading ? 'Caricamento documento…' : deliveryStatus }}
      </p>
      <label v-if="canUpload" class="chat-composer__attach" title="Allega un documento">
        <input
          type="file"
          accept="application/pdf, image/jpeg, image/png"
          class="upload-input"
          :disabled="isSending || isUploading"
          @change="uploadDocument"
        />
        <span class="material-icons" aria-hidden="true">attach_file</span>
        <span class="sr-only">Allega un documento</span>
      </label>

      <textarea
        ref="composerInput"
        v-model="newResponse"
        class="chat-composer__input"
        placeholder="Scrivi un messaggio…"
        rows="1"
        maxlength="20000"
        aria-label="Scrivi un messaggio"
        @input="resizeComposer"
        @keydown.enter.exact="submitResponse"
      ></textarea>

      <button
        type="button"
        class="chat-composer__send"
        :disabled="!ticketReady || isSending || isUploading || !newResponse.trim()"
        :aria-label="isSending ? 'Invio in corso' : 'Invia messaggio'"
        @click="submitResponse"
      >
        <span class="material-icons" :class="{ 'chat-composer__spinner': isSending }" aria-hidden="true">{{ isSending ? 'sync' : 'send' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onBeforeUnmount, computed, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  addDoc,
  getFirestore,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import DOMPurify from 'dompurify';
import { useChatComposer } from '@/composables/useChatComposer';
import { useChatDelivery } from '@/composables/useChatDelivery';
import UserAppChrome from './UserAppChrome.vue';
import { mergeTechnicalConversation, toDateValue } from '@/lib/technicalConversation';
import { sendTechnicalTicketMessage, createTechnicalMessageId } from '@/lib/technicalTicketMessages';

export default {
  name: 'UserTicketConversationTecnico',
  components: { UserAppChrome },
  setup() {
    const route = useRoute();
    const ticketId = computed(() => route.params.ticketId);
    const db = getFirestore(); 

    const conversations = ref([]);
    const documents = ref([]);
    const conversationContainer = ref(null);
    const lastMessageRef = ref(null);
    const pageRef = ref(null);
    const composerRef = ref(null);
    const composerInput = ref(null);
    const newResponse = ref('');
    const isUploading = ref(false);
    const ticketReady = ref(false);
    const connectionError = ref('');
    let ticketUnsubscribe = null;
    let documentsUnsubscribe = null;
    let subscriptionGeneration = 0;
    let subscribedUserId = null;
    let activeUpload = null;
    const { resizeComposer } = useChatComposer({
      pageRef,
      composerRef,
      inputRef: composerInput,
      draft: newResponse,
    });
    const ticketStatus = ref('');
    const user = ref({}); // Inizializza user come oggetto vuoto
    const canUpload = computed(() => user.value.canUploadTechnicalFiles === true);
    const uploadError = ref('');




// Computed property per filtrare i documenti con visibilityProfile = true
const filteredDocuments = computed(() => {
  return documents.value.filter(doc => doc.visibilityProfile === true && doc.uploadCategory === "Tecnico");
});


    const mergedConversations = computed(() => mergeTechnicalConversation(
      delivery.visibleConversations(conversations.value),
      filteredDocuments.value
    ));

    const setLastMessageEl = (el, index, total) => {
      if (index === total - 1) {
        lastMessageRef.value = el;
      }
    };

    const formatMessageDate = (timestamp) => {
      const date = toDateValue(timestamp);
      if (!date) return '';
      return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };
    const messageDateTime = (timestamp) => toDateValue(timestamp)?.toISOString();

    const messageKind = (item = {}) => {
      if (item.type === 'Richiesta') return 'outgoing';
      if (item.type === 'Trainer') return 'incoming';
      return 'system';
    };

    const messageAuthor = (item = {}) => {
      if (item.type === 'Richiesta') return 'Tu';
      if (item.type === 'Trainer') return 'Trainer';
      return 'Team Fusco Fit';
    };

    const messageIcon = (item = {}) => {
      if (item.type === 'Richiesta') return 'person';
      if (item.type === 'Trainer') return 'fitness_center';
      return 'notifications_none';
    };

    const scrollToBottom = async () => {
      if (typeof window === 'undefined') return;
      await nextTick();
      const target = lastMessageRef.value || conversationContainer.value;
      if (!target) return;
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'auto', block: 'end' });
      } else if (conversationContainer.value) {
        conversationContainer.value.scrollTop = conversationContainer.value.scrollHeight;
      }
    };

    const delivery = useChatDelivery({
      ticketId,
      send: sendTechnicalTicketMessage,
      createId: createTechnicalMessageId,
      onQueued: () => { newResponse.value = ''; scrollToBottom(); },
      onConfirmed: () => { scrollToBottom(); },
    });
    const { isSending, deliveryStatus } = delivery;
    const retryMessage = (id) => {
      if (isUploading.value) return;
      return delivery.retry(id);
    };

    const subscribeDocuments = (userId, generation) => {
      if (documentsUnsubscribe) documentsUnsubscribe();
      documents.value = [];
      documentsUnsubscribe = null;
      if (!userId) return;

      const documentsQuery = query(collection(db, 'documenti'), where('userId', '==', userId));
      documentsUnsubscribe = onSnapshot(documentsQuery, (snapshot) => {
        if (generation !== subscriptionGeneration) return;
        documents.value = snapshot.docs.map((documentSnapshot) => ({
          id: documentSnapshot.id,
          ...documentSnapshot.data()
        }));
      }, (error) => {
        console.error('Errore durante il recupero dei documenti:', error);
      });
    };

    const loadTicketUser = async (userId, generation) => {
      subscribeDocuments(userId, generation);
      if (!userId) return;
      try {
        const userSnapshot = await getDoc(doc(db, 'users', userId));
        if (generation !== subscriptionGeneration || subscribedUserId !== userId) return;
        if (userSnapshot.exists()) {
          user.value = { ...userSnapshot.data(), uid: userId };
        } else {
          console.error('Utente non trovato');
        }
      } catch (error) {
        console.error('Errore durante il recupero dei dettagli utente:', error);
      }
    };

    const subscribeConversation = (activeTicketId) => {
      const generation = ++subscriptionGeneration;
      if (ticketUnsubscribe) ticketUnsubscribe();
      if (documentsUnsubscribe) documentsUnsubscribe();
      ticketUnsubscribe = null;
      documentsUnsubscribe = null;
      subscribedUserId = null;
      conversations.value = [];
      documents.value = [];
      user.value = {};
      ticketStatus.value = '';
      ticketReady.value = false;
      newResponse.value = '';
      lastMessageRef.value = null;
      activeUpload = null;
      isUploading.value = false;
      delivery.reset();
      uploadError.value = '';
      connectionError.value = '';
      if (!activeTicketId) return;

      ticketUnsubscribe = onSnapshot(doc(db, 'assistenzatecnica', activeTicketId), { includeMetadataChanges: true }, (snapshot) => {
        if (generation !== subscriptionGeneration) return;
        if (!snapshot.exists()) {
          if (snapshot.metadata?.fromCache) return;
          conversations.value = [];
          ticketStatus.value = '';
          if (documentsUnsubscribe) documentsUnsubscribe();
          documentsUnsubscribe = null;
          subscribedUserId = '';
          documents.value = [];
          user.value = {};
          ticketReady.value = false;
          if (!snapshot.metadata?.fromCache) connectionError.value = 'Questa conversazione non è disponibile. Riapri l’assistenza.';
          console.error('Ticket non trovato');
          return;
        }
        const data = snapshot.data();
        conversations.value = Array.isArray(data.conversations) ? data.conversations : [];
        delivery.reconcile(conversations.value, snapshot.metadata);
        ticketReady.value = true;
        connectionError.value = '';
        ticketStatus.value = data.status || '';
        if (subscribedUserId !== (data.userId || '')) {
          subscribedUserId = data.userId || '';
          user.value = {};
          loadTicketUser(data.userId, generation);
        }
      }, (error) => {
        if (generation !== subscriptionGeneration) return;
        console.error('Errore durante il recupero della conversazione:', error);
        connectionError.value = 'Non riesco ad aggiornare la chat. Controlla la connessione e riapri questa pagina.';
      });
    };

    const uploadDocument = async (event) => {
      if (isSending.value || isUploading.value || !canUpload.value) return;
      const file = event.target.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        uploadError.value = 'Il file supera i 10MB.';
        return;
      }
      const allowedFileTypes = ['pdf', 'jpg', 'jpeg', 'png'];
      const fileType = allowedFileTypes.find(type => file.type.includes(type));
      if (!fileType) {
        uploadError.value = 'Formato non supportato. Usa solo PDF, JPG, JPEG o PNG.';
        return;
      }
      const userId = user.value.uid;
      const activeTicketId = ticketId.value;
      if (!userId) {
        uploadError.value = 'Impossibile recuperare lo userId.';
        return;
      }

      let documentSaved = false;
      const operation = {};
      activeUpload = operation;
      isUploading.value = true;
      uploadError.value = '';
      try {
        const storage = getStorage();
        const fileRef = storageRef(storage, `documenti/${userId}/${file.name}`);
        await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(fileRef);
        await addDoc(collection(db, 'documenti'), {
          userId,
          fileUrl,
          fileName: file.name,
          fileType,
          uploadedAt: serverTimestamp(),
          uploadUser: 'Utente',
          visibilityProfile: true,
          uploadCategory: 'Tecnico'
        });
        documentSaved = true;
        await sendTechnicalTicketMessage({ ticketId: activeTicketId, content: 'File caricato' });
        if (activeUpload === operation && ticketId.value === activeTicketId) {
          uploadError.value = '';
          await scrollToBottom();
        }
      } catch (error) {
        console.error('Errore durante il caricamento:', error);
        if (activeUpload === operation && ticketId.value === activeTicketId) {
          uploadError.value = documentSaved
            ? "Il documento è caricato, ma l'assistenza non è stata notificata. Invia un messaggio per segnalarlo."
            : 'Errore nel caricamento del documento.';
        }
      } finally {
        if (activeUpload === operation) {
          activeUpload = null;
          isUploading.value = false;
          event.target.value = '';
        }
      }
    };

const convertLinks = (text = '') => {
  if (!text) return '';

  const cleanHref = (raw = '') => {
    let href = raw.trim();
    if (href.startsWith('<')) href = href.slice(1);
    if (href.endsWith('>')) href = href.slice(0, -1);
    href = href.replace(/[),.;:]+$/, '');
    if (href && !href.startsWith('http')) href = `http://${href}`;
    return href;
  };

  // Markdown [label](url)
  const markdownPattern = /\[([^\]]+)\]\(\s*<?(https?:\/\/[^\s)<>]+|www\.[^\s)<>]+)>?\s*\)/gi;
  let transformed = text.replace(markdownPattern, (_match, label, url) => {
    const href = cleanHref(url);
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="formatted-link">${label}</a>`;
  });

  // Link plain, evitando di riformattare link già dentro tag <a ...>
  const urlPattern = /<?\b(https?:\/\/[^\s<>]+|www\.[^\s<>]+)>?/gi;
  transformed = transformed.replace(urlPattern, (match, url, offset, original) => {
    const before = original.slice(0, offset);
    const lastOpen = before.lastIndexOf('<a ');
    const lastClose = before.lastIndexOf('</a>');
    const insideExistingAnchor = lastOpen !== -1 && lastOpen > lastClose;
    if (insideExistingAnchor) return match;

    const href = cleanHref(url);

    if (href.includes('firebasestorage.googleapis.com')) {
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="formatted-link">Visualizza Link</a>`;
    }

    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="formatted-link">${url}</a>`;
  });

  return transformed;
};
    const sanitizeMessageContent = (content) => {
      const contentWithLinks = convertLinks(content);
      return DOMPurify.sanitize(contentWithLinks);
    };

    const submitResponse = (event) => {
      if (event?.isComposing || event?.keyCode === 229) return;
      if (event?.key === 'Enter') event.preventDefault();
      if (!ticketReady.value || isSending.value || isUploading.value) return;
      const content = newResponse.value.trim();
      if (!content) return;
      uploadError.value = '';
      return delivery.submit(content);
    };

    watch(ticketId, subscribeConversation, { immediate: true });
    onBeforeUnmount(() => {
      subscriptionGeneration += 1;
      activeUpload = null;
      delivery.dispose();
      if (ticketUnsubscribe) ticketUnsubscribe();
      if (documentsUnsubscribe) documentsUnsubscribe();
    });

    watch(
      () => mergedConversations.value.length,
      () => { scrollToBottom(); },
    );

    return {
      conversations,
      pageRef,
      composerRef,
      composerInput,
      newResponse,
      isSending,
      deliveryStatus,
      retryMessage,
      ticketReady,
      connectionError,
      isUploading,
      resizeComposer,
      submitResponse,
      ticketStatus,
      sanitizeMessageContent,
      formatMessageDate,
      messageDateTime,
      messageAuthor,
      messageIcon,
      messageKind,
      canUpload,
      mergedConversations,
      uploadDocument,
      uploadError,
      setLastMessageEl,
    };
  },
};
</script>

<style scoped>

.file-upload-section {
  margin: 20px 0;
  padding: 15px;
  background-color: #3d3d3d;
  border: 1px solid #1e1e1e;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.file-upload-section h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: #ffffff;
}

.upload-input {
  display: block;
  margin: 0 auto;
  padding: 8px 12px;
  font-size: 1rem;
  color: #555;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 5px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.upload-input:hover {
  border-color: #888;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.upload-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
}


.white-text {
  color: white;
}

.message-content a {
  color: white;
  font-weight: bold;
  text-decoration: underline;
}

.material-icons{
  color:#dedede
}

.trainer-message {
  background-color: #005f23;  /* Colore di sfondo verde chiaro per i messaggi del trainer */
  color: #f3f3f3 ;  /* Colore del testo scuro */
  border-radius: 14px;
  border: 0px;
  padding: 20px;
}

.user-message {
  background-color: #ffffff;  /* Colore di sfondo verde chiaro per i messaggi del trainer */
  color: #1d1d1d ;  /* Colore del testo scuro */
  border-radius: 14px;
  border: 0px;
  padding: 20px;
}


/* Contenitore dei messaggi - centrato */
.ticket-container {
  width: 100%;
  max-width: 800px;
  padding: 0;
  list-style: none;
  margin: 0 auto; /* Centra il contenitore */
  align-items: center; /* Centra i messaggi all'interno */
}

/* Stile generale dei messaggi */
.ticket-container li {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  padding: 15px;
  margin: 10px 0;
  text-align: left;
  word-break: break-word;
}

/* Contenitore dei bottoni: centrato e con spaziatura */
.button-container {
  display: flex;
  justify-content: center;
  gap: 15px; /* Spazio tra i bottoni */
  margin-top: 20px;
  flex-wrap: wrap; /* Permette ai bottoni di andare a capo su schermi piccoli */
}

/* Stile generico per i bottoni */
.action-button {
  padding: 12px 20px;
  align-items: center;
  border: none;
  background: rgba(235, 235, 235, 0.15);
  color: white;
  cursor: pointer;
  border-radius: 24px;
  font-size: 16px;
  font-weight: bold;
  transition: background 0.3s ease, transform 0.2s ease;
  text-align: center;
  min-width: 140px; /* Larghezza minima per evitare che siano troppo piccoli */
  margin-right: 0px !important;
}

/* Effetto hover */
.action-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.05);
}

/* Effetto attivo (quando cliccato) */
.action-button:active {
  transform: scale(0.95);
}

/* Media Queries per la responsività, mantenendo alcune delle tue configurazioni */
@media (max-width: 768px) {



.file-upload-section {
    padding: 10px; /* Ridotto per dispositivi mobili */
    border-radius: 6px;
  }

  .file-upload-section h2 {
    font-size: 1rem; /* Adattato per schermi piccoli */
  }

  .upload-input {
    width: 90%; /* Si adatta al contenitore */
    padding: 8px; /* Ridotto per dispositivi mobili */
    font-size: 0.9rem;
  }

  .ticket-container li {
    max-width: 100%;
  }
}

/* Fusco Fit chat experience */
.messages-page {
  --ff-bg: #f5f4f0;
  --ff-surface: rgba(255, 255, 255, 0.76);
  --ff-white: #ffffff;
  --ff-black: #111110;
  --ff-muted: #66635f;
  --ff-line: rgba(17, 17, 16, 0.1);
  --ff-red: #c8102e;
  --ff-red-dark: #9e0b23;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
  color: var(--ff-black);
  background:
    radial-gradient(circle at 92% 8%, rgba(200, 16, 46, 0.1), transparent 24rem),
    radial-gradient(circle at 4% 76%, rgba(200, 16, 46, 0.05), transparent 26rem),
    var(--ff-bg);
  font-family: 'DM Sans', Avenir, Helvetica, Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}

.messages-page,
.messages-page *,
.messages-page *::before,
.messages-page *::after {
  box-sizing: border-box;
}

.messages-page::before {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  content: '';
  opacity: 0.24;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.08'/%3E%3C/svg%3E");
}

.messages-shell {
  position: relative;
  z-index: 1;
  display: grid;
  align-content: start;
  gap: 18px;
  width: min(100%, 980px);
  margin: 0 auto;
  padding: clamp(34px, 6vw, 72px) clamp(20px, 5vw, 52px) 148px;
}

.messages-shell > .user-section-header {
  margin-bottom: 0;
}

.chat-panel {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.92);
  border-radius: 25px;
  background: var(--ff-surface);
  box-shadow:
    0 24px 60px rgba(47, 38, 32, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(22px) saturate(140%);
  -webkit-backdrop-filter: blur(22px) saturate(140%);
}

.chat-panel__header {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 34px;
  gap: 12px;
  align-items: center;
  min-height: 72px;
  padding: 12px 16px;
  color: var(--ff-white);
  background:
    radial-gradient(circle at 100% 0, rgba(200, 16, 46, 0.52), transparent 12rem),
    var(--ff-black);
  box-shadow: 0 10px 30px rgba(17, 17, 16, 0.14);
}

.chat-panel__avatar {
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.chat-panel__avatar .material-icons,
.chat-panel__secure.material-icons {
  color: var(--ff-white);
}

.chat-panel__avatar .material-icons {
  font-size: 22px;
}

.chat-panel__identity {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.chat-panel__identity strong {
  overflow: hidden;
  font-size: 0.96rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-panel__identity span {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.7rem;
}

.chat-panel__identity i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #61c987;
  box-shadow: 0 0 0 3px rgba(97, 201, 135, 0.12);
}

.chat-panel__identity i.is-complete {
  background: #b6b2ab;
  box-shadow: 0 0 0 3px rgba(182, 178, 171, 0.12);
}

.chat-panel__secure {
  justify-self: end;
  font-size: 19px;
  opacity: 0.68;
}

.chat-scroll {
  position: relative;
  height: min(58vh, 620px);
  min-height: 390px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 18px clamp(12px, 3vw, 24px) 22px;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  background:
    radial-gradient(circle at 1px 1px, rgba(17, 17, 16, 0.035) 1px, transparent 0) 0 0 / 20px 20px,
    linear-gradient(155deg, rgba(245, 244, 240, 0.72), rgba(236, 233, 227, 0.62));
  scrollbar-color: rgba(17, 17, 16, 0.2) transparent;
  scrollbar-width: thin;
}

.completed-text {
  width: fit-content;
  max-width: 88%;
  margin: 0 auto 16px;
  padding: 7px 11px;
  color: var(--ff-muted);
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  font-size: 0.7rem;
  text-align: center;
}

.messages-page .ticket-container {
  display: flex;
  width: 100%;
  max-width: none;
  flex-direction: column;
  gap: 9px;
  align-items: stretch;
  margin: 0;
  padding: 0;
  list-style: none;
}

.messages-page .ticket-container .chat-message {
  position: relative;
  display: flex;
  width: fit-content;
  max-width: min(82%, 660px);
  flex-direction: column;
  gap: 7px;
  margin: 0;
  padding: 11px 13px 8px;
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.07);
  border-radius: 18px 18px 18px 5px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 8px 20px rgba(47, 38, 32, 0.07);
  font-size: 0.88rem;
  line-height: 1.42;
  text-align: left;
  word-break: break-word;
}

.messages-page .ticket-container .chat-message--outgoing {
  align-self: flex-end;
  color: var(--ff-white);
  border-color: rgba(200, 16, 46, 0.22);
  border-radius: 18px 18px 5px 18px;
  background: linear-gradient(145deg, #d4143b, var(--ff-red-dark));
  box-shadow: 0 9px 22px rgba(158, 11, 35, 0.2);
}

.messages-page .ticket-container .chat-message--incoming {
  align-self: flex-start;
}

.messages-page .ticket-container .chat-message--system {
  align-self: flex-start;
  max-width: min(92%, 740px);
  border-color: rgba(17, 17, 16, 0.09);
  background: rgba(232, 230, 225, 0.94);
}

.chat-message__author {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--ff-muted);
  font-size: 0.6875rem;
  line-height: 1;
}

.chat-message__author .material-icons {
  color: var(--ff-red);
  font-size: 14px;
}

.chat-message--outgoing .chat-message__author {
  color: rgba(255, 255, 255, 0.7);
}

.chat-message--outgoing .chat-message__author .material-icons {
  color: rgba(255, 255, 255, 0.78);
}

.chat-message__content {
  color: inherit;
  white-space: pre-wrap;
}

.chat-message__content :deep(a),
.chat-message__content :deep(.formatted-link) {
  color: var(--ff-red-dark);
  font-weight: 700;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  overflow-wrap: anywhere;
}

.chat-message--outgoing .chat-message__content :deep(a),
.chat-message--outgoing .chat-message__content :deep(.formatted-link) {
  color: var(--ff-white);
}

.chat-message time {
  align-self: flex-end;
  color: rgba(17, 17, 16, 0.46);
  font-size: 0.6875rem;
  line-height: 1;
  white-space: nowrap;
}

.chat-message--outgoing time {
  color: rgba(255, 255, 255, 0.66);
}

.attached-file {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 20px;
  gap: 9px;
  align-items: center;
  min-width: min(260px, 100%);
  padding: 10px;
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  text-decoration: none;
}

.attached-file > .material-icons:first-child {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  color: var(--ff-red);
  border-radius: 10px;
  background: rgba(200, 16, 46, 0.08);
  font-size: 18px;
}

.attached-file > .material-icons:last-child {
  color: var(--ff-muted);
  font-size: 16px;
}

.attached-file span:nth-child(2) {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.attached-file small {
  color: var(--ff-muted);
  font-size: 0.6875rem;
}

.attached-file strong {
  overflow: hidden;
  font-size: 0.76rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-error {
  margin: 0;
  padding: 9px 16px;
  color: var(--ff-red-dark);
  border-top: 1px solid rgba(200, 16, 46, 0.12);
  background: rgba(200, 16, 46, 0.06);
  font-size: 0.75rem;
  font-weight: 700;
}

.chat-composer {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) 46px;
  gap: 9px;
  align-items: end;
  min-height: 72px;
  padding: 11px 13px;
  border-top: 1px solid rgba(17, 17, 16, 0.08);
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.chat-composer__attach,
.chat-composer__send {
  display: grid;
  width: 46px;
  height: 46px;
  min-width: 46px;
  min-height: 46px;
  margin: 0;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}

.chat-composer__attach {
  color: var(--ff-muted);
  border: 1px solid rgba(17, 17, 16, 0.1);
  background: rgba(255, 255, 255, 0.76);
}

.chat-composer__attach .material-icons {
  color: currentColor;
  font-size: 21px;
}

.messages-page .upload-input {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  white-space: nowrap;
}

.chat-composer__input {
  width: 100%;
  min-height: 46px;
  max-height: 128px;
  resize: vertical;
  margin: 0;
  padding: 12px 15px;
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.1);
  border-radius: 23px;
  background: rgba(245, 244, 240, 0.86);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.4;
  outline: none;
}

.chat-composer__input:focus {
  border-color: rgba(200, 16, 46, 0.45);
  box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.08);
}

.chat-composer__send {
  color: var(--ff-white);
  background: var(--ff-red);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.22);
  transition: transform 180ms ease, background 180ms ease, opacity 180ms ease;
}

.chat-composer__send:hover {
  background: var(--ff-red-dark);
  transform: translateY(-1px);
}

.chat-composer__send:disabled {
  color: #97938d;
  background: #e1dfda;
  box-shadow: none;
  cursor: not-allowed;
}

.chat-composer__send .material-icons {
  color: currentColor;
  font-size: 20px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.messages-page :is(button, textarea, label, a):focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.28);
  outline-offset: 3px;
}

@media (max-width: 719px) {
  .messages-shell {
    gap: 12px;
    padding: 28px 10px 138px;
  }

  .chat-panel {
    border-radius: 20px;
  }

  .chat-panel__header {
    grid-template-columns: 42px minmax(0, 1fr) 26px;
    min-height: 64px;
    padding: 9px 12px;
  }

  .chat-panel__avatar {
    width: 42px;
    height: 42px;
  }

  .chat-scroll {
    height: clamp(280px, 37dvh, 340px);
    min-height: 0;
    padding: 14px 10px 18px;
  }

  .messages-page .ticket-container .chat-message {
    max-width: 86%;
    padding: 10px 11px 7px;
    font-size: 0.84rem;
  }

  .messages-page .ticket-container .chat-message--system {
    max-width: 94%;
  }

  .chat-composer {
    grid-template-columns: auto minmax(0, 1fr) 42px;
    gap: 7px;
    min-height: 66px;
    padding: 9px;
  }

  .chat-composer__attach,
  .chat-composer__send {
    width: 42px;
    height: 42px;
    min-width: 42px;
    min-height: 42px;
  }

  .chat-composer__input {
    min-height: 42px;
    padding: 10px 13px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chat-scroll {
    scroll-behavior: auto;
  }

  .chat-composer__send {
    transition: none;
  }
}

/* Conversation-only layout: page scroll, compact fixed composer, no nested panels. */
.messages-shell {
  display: block;
  width: min(100%, 820px);
  margin: 0 auto;
  padding: 18px clamp(14px, 4vw, 32px) calc(82px + env(safe-area-inset-bottom));
}

.messages-page .ticket-container {
  gap: 10px;
}

.messages-page .ticket-container .chat-message {
  scroll-margin-bottom: 78px;
}

.upload-error {
  width: fit-content;
  max-width: 100%;
  margin: 12px auto 0;
  border: 1px solid rgba(200, 16, 46, 0.12);
  border-radius: 12px;
}

.chat-composer {
  position: fixed;
  z-index: 1100;
  right: auto;
  bottom: max(10px, env(safe-area-inset-bottom));
  left: 50%;
  display: flex;
  width: min(calc(100% - 20px), 800px);
  min-height: 52px;
  gap: 7px;
  align-items: center;
  padding: 6px;
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 16px 42px rgba(17, 17, 16, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.92);
  transform: translateX(-50%);
  backdrop-filter: blur(24px) saturate(1.55);
  -webkit-backdrop-filter: blur(24px) saturate(1.55);
}

.chat-composer__attach,
.chat-composer__send {
  width: 38px;
  height: 38px;
  min-width: 38px;
  min-height: 38px;
}

.chat-composer__input {
  width: auto;
  height: 38px;
  min-width: 0;
  min-height: 38px;
  max-height: 76px;
  flex: 1;
  margin: 0;
  padding: 8px 13px;
  overflow-y: hidden;
  border-radius: 18px;
  resize: none;
  font-size: 0.84rem;
  scrollbar-color: rgba(17, 17, 16, 0.24) transparent;
  scrollbar-width: thin;
}

.chat-composer__attach .material-icons,
.chat-composer__send .material-icons {
  font-size: 18px;
}

@media (max-width: 719px) {
  .messages-shell {
    padding: 14px 12px calc(78px + env(safe-area-inset-bottom));
  }

  .messages-page .ticket-container .chat-message {
    max-width: 88%;
  }

  .messages-page .ticket-container .chat-message--system {
    max-width: 95%;
  }
}
</style>

<style src="./styles/chatComposer.css" scoped></style>
